/**
 * Add / Edit project labour. Used by the Attendance tab.
 *
 * Fields (mirror mobile add-labour form):
 *   - Name *           (text)
 *   - Role *           (text — e.g. Carpenter, Helper, Plumber)
 *   - Pay rate         (number, optional)
 *   - Pay unit         (day / hour toggle)
 *   - Description      (optional, free text)
 *
 * Create writes:
 *   - One `projectLabour/{projectId_labourId}` doc
 *   `labourId` is a client-generated short id (`lab_<timestamp>`) —
 *   mirrors mobile which uses the same pattern. No Cloud Function
 *   needed; Firestore rules allow any project member to write to
 *   `projectLabour` on a project they belong to.
 *
 * Edit writes:
 *   - Patches the same `projectLabour` doc
 *   - Optionally syncs the labour's name/role/pay across every
 *     `attendance` record on the project (matches mobile's
 *     `updateLabourAcrossProject` — we call it inline here).
 *
 * Delete:
 *   - Soft-disable via `setLabourDisabledAcrossProject(true)`.
 *   - Hard delete needs admin → not exposed on the form (mobile
 *     parity).
 */
import { useEffect, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  setLabourDisabledAcrossProject,
  upsertProjectLabour,
  updateProjectLabour,
  type ProjectLabour,
} from '@/lib/attendance';

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

export function LabourFormSlideOver({
  open,
  onClose,
  orgId,
  projectId,
  labour,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  projectId: string;
  /** Edit mode when set; create mode otherwise. */
  labour: ProjectLabour | null;
  /** Fires after a successful save (create + edit) with the labourId. */
  onSaved?: (labourId: string) => void;
}) {
  const { user } = useAuth();
  const isEdit = Boolean(labour);

  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [payRate, setPayRate] = useState<string>('');
  const [payUnit, setPayUnit] = useState<'day' | 'hour'>('day');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  // Hydrate on open + when labour changes.
  useEffect(() => {
    if (!open) return;
    if (labour) {
      setName(labour.labourName ?? '');
      setRole(labour.labourRole ?? '');
      setPayRate(
        typeof labour.payRate === 'number' ? String(labour.payRate) : '',
      );
      setPayUnit((labour.payUnit as 'day' | 'hour') || 'day');
      setDescription(labour.description ?? '');
    } else {
      setName('');
      setRole('');
      setPayRate('');
      setPayUnit('day');
      setDescription('');
    }
    setError(null);
    setConfirmRemove(false);
  }, [open, labour]);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!orgId || !user) return setError('Sign-in required.');
    if (name.trim().length < 2)
      return setError('Name must be at least 2 characters.');
    if (role.trim().length < 2)
      return setError('Role must be at least 2 characters.');
    const rateNum = payRate ? Number(payRate.replace(/,/g, '')) : undefined;
    if (rateNum !== undefined && (Number.isNaN(rateNum) || rateNum < 0))
      return setError('Pay rate must be a non-negative number.');

    setBusy(true);
    try {
      const labourId = isEdit && labour ? labour.labourId : `lab_${Date.now()}`;
      if (isEdit && labour) {
        await updateProjectLabour(projectId, labour.labourId, {
          labourName: name.trim(),
          labourRole: role.trim(),
          description: description.trim(),
          payRate: rateNum,
          payUnit,
        });
      } else {
        await upsertProjectLabour({
          orgId,
          projectId,
          labourId,
          labourName: name.trim(),
          labourRole: role.trim(),
          description: description.trim(),
          payRate: rateNum,
          payUnit,
          createdBy: user.uid,
          disabled: false,
        });
      }
      onSaved?.(labourId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!labour) return;
    setBusy(true);
    try {
      await setLabourDisabledAcrossProject(projectId, labour.labourId, true);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-user-circle-gear" />{' '}
            {isEdit ? 'Edit Worker' : 'Add Worker'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          <div>
            <label style={LABEL}>Name *</label>
            <input
              style={INPUT}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Ravi Kumar"
              autoFocus
            />
          </div>

          <div>
            <label style={LABEL}>Role *</label>
            <input
              style={INPUT}
              value={role}
              onChange={(e) => setRole(e.currentTarget.value)}
              placeholder="e.g. Carpenter, Helper, Plumber"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={LABEL}>Pay rate (₹)</label>
              <input
                style={INPUT}
                value={payRate}
                onChange={(e) =>
                  setPayRate(e.currentTarget.value.replace(/[^\d.]/g, ''))
                }
                placeholder="e.g. 900"
                inputMode="decimal"
              />
            </div>
            <div>
              <label style={LABEL}>Unit</label>
              <div
                className="pha-toggle"
                style={{ height: 38 }}
              >
                <button
                  type="button"
                  className={`pha-btn ${payUnit === 'day' ? 'active p' : ''}`}
                  onClick={() => setPayUnit('day')}
                  style={{
                    width: 'auto',
                    padding: '0 14px',
                    height: 38,
                    color: payUnit === 'day' ? '#fff' : 'var(--text-secondary)',
                    background: payUnit === 'day' ? 'var(--text-primary)' : 'transparent',
                  }}
                >
                  Day
                </button>
                <button
                  type="button"
                  className={`pha-btn ${payUnit === 'hour' ? 'active p' : ''}`}
                  onClick={() => setPayUnit('hour')}
                  style={{
                    width: 'auto',
                    padding: '0 14px',
                    height: 38,
                    color: payUnit === 'hour' ? '#fff' : 'var(--text-secondary)',
                    background: payUnit === 'hour' ? 'var(--text-primary)' : 'transparent',
                  }}
                >
                  Hour
                </button>
              </div>
            </div>
          </div>

          <div>
            <label style={LABEL}>Description</label>
            <textarea
              style={{ ...INPUT, minHeight: 70, resize: 'vertical' }}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder="Optional notes (skills, shift, contractor, etc.)"
            />
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            {isEdit ? (
              confirmRemove ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: 12, color: 'var(--critical)' }}>
                    Remove from roster?
                  </span>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setConfirmRemove(false)}
                    disabled={busy}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleRemove}
                    disabled={busy}
                    style={{
                      background: 'var(--critical)',
                      borderColor: 'var(--critical)',
                    }}
                  >
                    Yes, remove
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmRemove(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Remove
                </button>
              )
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmit}
                disabled={busy}
              >
                {busy
                  ? 'Saving…'
                  : isEdit
                    ? 'Save changes'
                    : 'Add to roster'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
